import { useState, type FormEvent } from 'react';
import { MIN_PASSWORD, useAuth } from '../../app/AuthProvider';
import { AuthCard, PasswordField } from './AuthCard';
import { Button, ErrorNote } from '../../ui/components';

/**
 * Where a password-reset link lands (V2 D82). The link carries a session
 * whose only job is this form, so there is no way to reach the app without
 * finishing it, and no way to reach this form without the link.
 */
export function ResetPasswordPage() {
  const { updatePassword, signOut, session } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) return setError(`Use at least ${MIN_PASSWORD} characters.`);
    if (password !== confirm) return setError('The two passwords do not match.');
    setBusy(true);
    try {
      const err = await updatePassword(password);
      if (err) setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title="Choose a new password">
      <p className="mb-4 text-sm text-ink-muted">
        Setting a password for <span className="font-medium text-ink">{session?.user.email ?? 'your account'}</span>.
      </p>
      <form onSubmit={(e) => void submit(e)} className="space-y-3">
        <PasswordField id="new-password" label="New password" value={password} onChange={setPassword} autoComplete="new-password" hint={`At least ${MIN_PASSWORD} characters.`} />
        <PasswordField
          id="confirm-password"
          label="Confirm password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          error={confirm.length > 0 && confirm !== password ? 'The two passwords do not match.' : undefined}
        />
        {error && <ErrorNote message={error} />}
        <Button type="submit" variant="primary" busy={busy} className="w-full justify-center py-2">Save password and continue</Button>
      </form>
      <div className="mt-4 text-sm">
        <button type="button" className="text-navy underline" onClick={() => void signOut()}>Cancel and sign out</button>
      </div>
    </AuthCard>
  );
}
